import '../styles/main.css';
import * as THREE from 'three';
import { params, DEFAULT_HEIGHTMAP_URL } from './config.js';
import {
  scene,
  initScene,
  applyYawPitchDeg,
  applyFogFromParams,
  setDefaultCameraState,
  setupResizeHandler
} from './scene.js';
import * as SceneModule from './scene.js';
import { airplaneState } from './airplane/state.js';
import { initAirplane, getAirplaneGroup } from './airplane/create.js';
import { updateAirplane, setupCrashOverlayHandler } from './airplane/update.js';
import { pointMaterial } from './terrain/material.js';
import { state, scrollOffset, rebuildTiling, updateTileMatrices, getTilesPoints } from './terrain/tiling.js';
import { applyHeightmapTexture, setupHeightmapHandlers } from './terrain/heightmap.js';
import { radarState, updateRadarPulse, updateVisualRings } from './effects/radar.js';
import { updateCameraMode } from './camera/modes.js';
import { setupKeyboardControls } from './controls/keyboard.js';
import { setupMouseControls, mouseNdc } from './controls/mouse.js';
import { setupTouchControls } from './controls/touch.js';
import { setupGyroControls } from './controls/gyro.js';
import { initCurvedGauges, updateSpeedUI, updateAltitudeUI, updateIndicatorPositions } from './ui/gauges.js';
import { setupTweakpane, pane } from './ui/tweakpane.js';
import { initBoostUI, updateBoostUI } from './ui/boost.js';

// Reusable objects for animation loop (avoid GC pressure)
const _raycaster = new THREE.Raycaster();
const _hitPoint = new THREE.Vector3();
const _groundPlane = new THREE.Plane(new THREE.Vector3(0, 1, 0), 0);
const _forward = new THREE.Vector3();

// Initialize from default parameters
function initFromDefaults() {
  const { camera, grid } = SceneModule;

  // Apply yaw/pitch
  applyYawPitchDeg(params.camYaw, params.camPitch);

  // Apply FOV
  camera.fov = params.camFov;
  camera.updateProjectionMatrix();

  // Apply height & camera Z
  camera.position.y = params.camHeight;
  camera.position.z = params.camZ;
  
  // scrollSpeed is now unified via getter/setter in state
  
  // Grid visibility
  grid.visible = false;

  // Scale
  pointMaterial.uniforms.uScale.value = params.scale;
  pointMaterial.uniforms.uBias.value = -params.scale * 0.5;

  // Point color default
  pointMaterial.uniforms.uColor.value.set(params.terrainColor);

  // Jitter default
  pointMaterial.uniforms.uJitter.value = params.jitter;

  // Point defaults
  pointMaterial.uniforms.uPointSize.value = params.pointSize;
  pointMaterial.uniforms.uMaskRadius.value = params.maskRadius;
  pointMaterial.uniforms.uMaskFeather.value = params.maskFeather;

  // Hover defaults
  pointMaterial.uniforms.uHoverRadius.value = params.hoverRadius;
  pointMaterial.uniforms.uHoverStrength.value = params.hoverStrength;
  pointMaterial.uniforms.uHoverSnap.value = params.hoverSnap;
  pointMaterial.uniforms.uHoverColor.value.set(params.hoverColor);
  pointMaterial.uniforms.uHoverEnabled.value = params.mouseHoverEnabled ? 1.0 : 0.0;

  // Airplane hover defaults
  pointMaterial.uniforms.uAirplaneHoverEnabled.value = params.airplaneHoverEnabled ? 1.0 : 0.0;
  pointMaterial.uniforms.uAirplaneHoverRadius.value = params.airplaneHoverRadius;

  // Record defaults for Reset
  setDefaultCameraState();

  // Initialize fog state
  applyFogFromParams();

  // Compile shader after defines updates
  pointMaterial.needsUpdate = true;

  // Load default heightmap texture
  const textureLoader = new THREE.TextureLoader();
  textureLoader.load(
    DEFAULT_HEIGHTMAP_URL,
    (tex) => {
      applyHeightmapTexture(tex).catch((err) => console.error('Failed to apply default heightmap', err));
    },
    undefined,
    (err) => {
      console.error('Failed to load default heightmap', err);
    }
  );
}

// Main animation loop
function animate() {
  requestAnimationFrame(animate);

  const { camera, clock, renderer } = SceneModule;

  const delta = clock.getDelta();
  pointMaterial.uniforms.uTime.value = performance.now() * 0.001;
  
  // Update hover center from mouse ray on ground plane (using pooled objects)
  _raycaster.setFromCamera(mouseNdc, camera);
  
  if (_raycaster.ray.intersectPlane(_groundPlane, _hitPoint)) {
    pointMaterial.uniforms.uHoverCenter.value.set(_hitPoint.x, _hitPoint.z);
  } else {
    pointMaterial.uniforms.uHoverCenter.value.set(9999.0, 9999.0);
  }

  // Update airplane FIRST so heading is current before terrain scroll
  updateAirplane(delta);

  // Update terrain scroll
  const tilesPoints = getTilesPoints();
  if (params.animateScroll && tilesPoints && tilesPoints.length) {
    // Scroll direction follows airplane heading (heading 0 = -Z direction)
    _forward.set(
      Math.sin(airplaneState.heading),
      0,
      -Math.cos(airplaneState.heading)
    );
    const step = state.scrollSpeed * delta;
    scrollOffset.x += _forward.x * step;
    scrollOffset.y += _forward.z * step;
    updateTileMatrices();
  }

  // Update airplane hover position for shader
  const airplaneGroup = getAirplaneGroup();
  if (airplaneGroup && airplaneState.enabled) {
    pointMaterial.uniforms.uAirplanePos.value.copy(airplaneGroup.position);

    // Update mask center to follow airplane in chase-style modes
    const isChaseStyle = params.cameraMode === 'chase' || params.cameraMode === 'topdown' || params.cameraMode === 'isometric';
    if (isChaseStyle) {
      pointMaterial.uniforms.uMaskCenter.value.set(0, 0);
    }

    // Update UI indicators
    updateAltitudeUI();
    updateSpeedUI();
    updateIndicatorPositions();
    updateBoostUI();
  } else {
    pointMaterial.uniforms.uAirplanePos.value.set(9999, 9999, 9999);
  }

  // Update radar pulse
  updateRadarPulse();

  // Update all visual rings (multiple independent rings)
  updateVisualRings();

  // Update camera mode
  updateCameraMode(delta);

  // Render
  renderer.render(scene, camera);
}

// Initialize everything
function init() {
  console.log('Starting initialization...');

  // Initialize scene first (creates camera, renderer, etc.)
  console.log('Initializing scene...');
  if (!initScene()) {
    console.error('Failed to initialize scene');
    return;
  }
  console.log('Scene initialized successfully');

  // Build initial tiling
  console.log('Building terrain tiles...');
  rebuildTiling();

  // Initialize airplane
  console.log('Initializing airplane...');
  initAirplane();

  // Setup controls
  console.log('Setting up controls...');
  setupMouseControls();
  setupKeyboardControls();
  setupTouchControls();
  setupGyroControls();

  // Setup UI
  console.log('Setting up UI...');
  setupTweakpane();
  initCurvedGauges();
  setupCrashOverlayHandler();
  initBoostUI();

  // Setup handlers
  console.log('Setting up handlers...');
  setupHeightmapHandlers();
  setupResizeHandler();

  // Initialize from defaults
  console.log('Applying default settings...');
  initFromDefaults();

  console.log('Initialization complete, starting animation loop');
  // Start animation loop
  animate();
}

// Start the application when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    try {
      init();
    } catch (err) {
      console.error('Failed to initialize application:', err);
    }
  });
} else {
  // DOM already loaded
  try {
    init();
  } catch (err) {
    console.error('Failed to initialize application:', err);
  }
}
